import React, {
  Key,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Link, Navigate, useParams } from 'react-router-dom'

import './KycQueue.scss'

import iconSearch from '../../../../../Assets/Icon/AdminKycQueue/mna0yq0f-rbvml9l.svg'
import iconExport from '../../../../../Assets/Icon/AdminKycQueue/mna0yq0f-ni0bkoo.svg'
import iconPrev from '../../../../../Assets/Images/AdminKycQueue/mna0yq0f-gfll4cj.png'
import iconNext from '../../../../../Assets/Icon/AdminKycQueue/mna0yq0f-jzsnn6w.svg'

import kycServices, { type InvestorKycApplicationsStatus } from 'Services/kycServices'
import { TimeConverter } from 'Helpers/TimeConverter'
import { formatString } from 'Helpers/formatString'
import { HorizontalLoader } from 'Components'

type KycStatusRoute = 'pending' | 'approved' | 'rejected' | 'request-info'

export type InvestorKycApplication = {
  id: string
  accountId: string
  companyName: string
  businessType: string
  dateOfBirth: string
  gender: string
  nationality: string
  identityDocumentType: string
  documentNumber: string
  isPep: boolean
  pepDetails: string
  hasCriminalRecord: boolean
  criminalRecordDetails: string
  hasFinancialCrimeInvestigation: boolean
  verificationDocuments: {
    keyFromCloudProvider: string
    url: string
    fileUuid: string
    mimetype: string
    fileCloudProvider: string
    fileCategory: string
    metadata: {
      expiryDate: string
    }
  }[]
  status: string
  statusHistory: [
    {
      status: string
      dateTimeInISO8601: string
      metadata: {
        deliveryTruckIds: string[]
        deliveryFee: number
        unitPrice: number
      }
      actor: string
    },
  ]
  createdAt: string
}

const KycQueue: React.FC = () => {
  const { status } = useParams()
  const activeStatus = (status ?? 'pending') as KycStatusRoute

  const isValidStatus =
    activeStatus === 'pending' ||
    activeStatus === 'approved' ||
    activeStatus === 'rejected' ||
    activeStatus === 'request-info'

  const statusToApiStatus = useMemo<Record<KycStatusRoute, InvestorKycApplicationsStatus>>(
    () => ({
      pending: 'PENDING_APPROVAL',
      approved: 'APPROVED',
      rejected: 'REJECTED',
      'request-info': 'REQUEST_INFO',
    }),
    [],
  )

  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(20)

  const [msisdnInput, setMsisdnInput] = useState('')

  const [msisdn, setMsisdn] = useState<string | undefined>(undefined)

  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [statusCounts, setStatusCounts] = useState<Record<KycStatusRoute, number>>({
    pending: 0,
    approved: 0,
    rejected: 0,
    'request-info': 0,
  })

  const tabsRowRef = useRef<HTMLDivElement | null>(null)
  const tabRefs = useRef<Record<string, HTMLAnchorElement | null>>({})
  const [indicatorLeft, setIndicatorLeft] = useState(0)

  const setTabRef = useCallback(
    (key: KycStatusRoute) => (el: HTMLAnchorElement | null) => {
      tabRefs.current[key] = el
    },
    [],
  )

  useEffect(() => {
    if (msisdnInput.length >= 5) {
      const timer = setTimeout(() => {
        if (!loading) {
          setMsisdn(msisdnInput)
        }
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [msisdnInput, loading])

  const [paginationData, setPaginationData] = useState<{
    pageNumber: number
    pageSize: number
    total: number
  }>({
    pageNumber: 1,
    pageSize: 20,
    total: 0,
  })

  const [rows, setRows] = useState<InvestorKycApplication[]>([])

  useEffect(() => {
    if (!isValidStatus) return

    const fetchApplications = async () => {
      setLoading(true)
      setErrorMessage(null)
      try {
        const response = await kycServices.listInvestorKycApplications({
          pageNumber,
          pageSize,
          status: statusToApiStatus[activeStatus],
          msisdn,
        })

        const data = response?.data?.data
        const dataset = (data?.dataset ?? []) as InvestorKycApplication[]

        setRows(dataset)
        setPaginationData({
          pageNumber: Number(data?.pageNumber ?? pageNumber),
          pageSize: Number(data?.pageSize ?? pageSize),
          total: Number(data?.total ?? 0),
        })
      } catch (e) {
        setRows([])
        setPaginationData({ pageNumber, pageSize, total: 0 })
        const maybeMessage =
          typeof e === 'object' && e !== null && 'message' in e ? String((e as any).message) : null
        setErrorMessage(maybeMessage ?? 'Failed to load KYC applications')
      } finally {
        setLoading(false)
      }
    }

    fetchApplications()
  }, [activeStatus, isValidStatus, msisdn, pageNumber, pageSize, statusToApiStatus])

  useEffect(() => {
    if (!isValidStatus) return
    setPageNumber(1)
  }, [activeStatus, isValidStatus, msisdn])

  useEffect(() => {
    if (!isValidStatus) return
    let cancelled = false

    const fetchCounts = async () => {
      const statuses: KycStatusRoute[] = ['pending', 'approved', 'rejected', 'request-info']
      const results = await Promise.allSettled(
        statuses.map((s) =>
          kycServices.listInvestorKycApplications({
            pageNumber: 1,
            pageSize: 1,
            status: statusToApiStatus[s],
            msisdn,
          }),
        ),
      )

      if (cancelled) return

      const next: Record<KycStatusRoute, number> = {
        pending: 0,
        approved: 0,
        rejected: 0,
        'request-info': 0,
      }

      statuses.forEach((s, idx) => {
        const r = results[idx]
        if (r.status === 'fulfilled') {
          const total = Number(r.value?.data?.data?.total ?? 0)
          next[s] = Number.isFinite(total) ? total : 0
        }
      })

      setStatusCounts(next)
    }

    fetchCounts()
    return () => {
      cancelled = true
    }
  }, [isValidStatus, msisdn, statusToApiStatus])

  useLayoutEffect(() => {
    const row = tabsRowRef.current
    const el = tabRefs.current[activeStatus]
    if (!row || !el) return
    setIndicatorLeft(el.offsetLeft - row.scrollLeft)
  }, [activeStatus, statusCounts])

  if (!isValidStatus) return <Navigate to='/blockchain/kyc-queue/pending' replace />

  const totalPages = Math.max(1, Math.ceil(paginationData.total / paginationData.pageSize))

  const tabs: Array<{ key: KycStatusRoute; to: string; label: string }> = [
    {
      key: 'pending',
      to: '/blockchain/kyc-queue/pending',
      label: `Pending (${statusCounts.pending})`,
    },
    {
      key: 'approved',
      to: '/blockchain/kyc-queue/approved',
      label: `Approved (${statusCounts.approved})`,
    },
    {
      key: 'rejected',
      to: '/blockchain/kyc-queue/rejected',
      label: `Rejected (${statusCounts.rejected})`,
    },
    // {
    //   key: 'request-info',
    //   to: '/blockchain/kyc-queue/request-info',
    //   label: `Request Info (${statusCounts['request-info']})`,
    // },
  ]

  return (
    <div className='admin-kyc'>
      <div className='admin-kyc__title'>KYC Applications</div>

      <div className='admin-kyc-status-tabs' role='tablist' aria-label='KYC status filter'>
        <div className='admin-kyc-status-tabs__row' ref={tabsRowRef}>
          {tabs.map((t) => (
            <Link
              key={t.key}
              to={t.to}
              role='tab'
              aria-selected={activeStatus === t.key}
              className={[
                'admin-kyc-status-tab',
                activeStatus === t.key ? 'admin-kyc-status-tab--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              ref={setTabRef(t.key)}
              onClick={() => setPageNumber(1)}
            >
              {t.label}
            </Link>
          ))}
        </div>
        <div
          className='admin-kyc-status-tabs__indicator'
          style={{ transform: `translateX(${indicatorLeft}px)` }}
        />
      </div>

      <div className='admin-kyc__toolbar'>
        <div className='admin-kyc-search'>
          <img src={iconSearch} alt='' className='admin-kyc-search__icon' />
          <input
            value={msisdnInput}
            onChange={(e) => setMsisdnInput(e.target.value)}
            className='admin-kyc-search__input'
            placeholder='Search by msisdn (e.g. +234...)'
          />
        </div>

        <button type='button' className='admin-kyc-button d-none'>
          <img src={iconExport} alt='' className='admin-kyc-button__icon' />
          <span>Export</span>
        </button>
      </div>

      <div className='card mt-2 border-0'>
        <div className='card-body'>
          <div className='table-responsive'>
            <table className='table'>
              <thead>
                <tr className=''>
                  <th className='fw-normal text-secondary'>S/N</th>
                  <th className='fw-normal text-secondary'>Company</th>
                  <th className='fw-normal text-secondary'>Business Type</th>
                  <th className='fw-normal text-secondary'>Status</th>
                  <th className='fw-normal text-secondary'>Date Submitted</th>
                  <th className='fw-normal text-secondary text-end'>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className='text-center'>
                      <HorizontalLoader></HorizontalLoader>
                    </td>
                  </tr>
                )}
                {!loading && errorMessage && (
                  <tr>
                    <td colSpan={6} className='text-center text-danger'>
                      {errorMessage}
                    </td>
                  </tr>
                )}
                {!loading && !errorMessage && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className='text-center'>
                      No applications found
                    </td>
                  </tr>
                )}
                {!loading &&
                  !errorMessage &&
                  rows.map((r: InvestorKycApplication, index: Key) => (
                    <tr key={index}>
                      <td>{Number(index) + 1}</td>
                      <td>{r.companyName ?? '-'}</td>
                      <td>{r.businessType ?? '-'}</td>
                      <td>{formatString(r.status ?? '-')}</td>
                      <td>{TimeConverter(r.createdAt)}</td>
                      <td className='text-end'>
                        <Link
                          to={`/blockchain/kyc-queue/${activeStatus}/${r.id}`}
                          className='admin-kyc-table__action'
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <div className='admin-kyc-pagination'>
              <button
                type='button'
                className='admin-kyc-pagination__button'
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={pageNumber <= 1 || loading}
              >
                <img src={iconPrev} alt='' className='admin-kyc-pagination__icon' />
                <span>Previous</span>
              </button>
              <div className='admin-kyc-pagination__numbers'>
                Page {pageNumber} of {totalPages}
              </div>
              <button
                type='button'
                className='admin-kyc-pagination__button'
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={pageNumber >= totalPages || loading}
              >
                <span>Next</span>
                <img src={iconNext} alt='' className='admin-kyc-pagination__icon' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KycQueue
